import { AuthEnums, MonthlyReportEnums } from "models/enums";
import useAPIClientHelper from "./useAPIClientHelper";
import { WintSuperkollAPIModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { AxiosResponse } from "axios";

// TODO (follow-up, PR #2612): generate this from the C# contract. It's hand-written because
// monthly-reports is a separate microservice behind the SuperkollAPI YARP proxy, so it isn't in the
// SuperkollAPI swagger that `wint-api-client` is generated from.
export interface MonthlyReportClientDto {
  id: string;
  companyId: number;
  periodStart: string;
  periodEnd: string;
  segment: string;
  currentIteration: number;
  currentMarkdown?: string;
  // Server-rendered HTML, injected with `dangerouslySetInnerHTML`. The server escapes raw HTML and
  // strips dangerous-scheme (javascript:/vbscript:/data:/file:) links before sending. Always present.
  currentRenderedHtml: string;
  approvedAt?: string;
  sentAt?: string;
}

/**
 * The company's choice of who is told when a monthly report is approved. Mirrors
 * `MonthlyReportRecipientDto` on the service side.
 */
export interface MonthlyReportRecipientDto {
  role: MonthlyReportEnums.RecipientRole;
  isEnabled: boolean;
}

const MONTHLY_REPORTS_REPORTS_PATH = "/api/services/monthly-reports/reports";
const MONTHLY_REPORTS_RECIPIENT_PATH = "/api/services/monthly-reports/report-recipient";

const useMonthlyReportService = () => {
  const { apiClientInstance } = useAPIClientHelper();

  // AuthType.Company: Bearer + `CompanyId` header. The SuperkollAPI YARP proxy validates the caller's
  // membership of that company and forwards a trusted `X-Wint-Company-Id` header, which the
  // monthly-reports service uses to scope the lookup (404 when it doesn't match the report's company).
  // Without this, the endpoint is an unscoped id lookup any logged-in user could read. A rejected
  // promise carries `{ status }` (404/403) from the shared error interceptor.
  const getReport = async (id: string): Promise<MonthlyReportClientDto> => {
    const response = await apiClientInstance(AuthEnums.AuthType.Company).get(
      `${MONTHLY_REPORTS_REPORTS_PATH}/${encodeURIComponent(id)}`
    );
    return response.data as MonthlyReportClientDto;
  };

  // Same AuthType.Company scoping as getReport: the service derives the company from the trusted
  // `X-Wint-Company-Id` header the proxy injects, so the caller can only ever read or change their
  // own company's setting — the company id is never a parameter the client gets to choose.
  //
  // A company that has never configured this gets the defaults (Admin, enabled) rather than a 404,
  // because that is what approval will actually do.
  const getRecipient = async (): Promise<MonthlyReportRecipientDto> => {
    const response = await apiClientInstance(AuthEnums.AuthType.Company).get(MONTHLY_REPORTS_RECIPIENT_PATH);
    return response.data as MonthlyReportRecipientDto;
  };

  const updateRecipient = async (recipient: MonthlyReportRecipientDto): Promise<MonthlyReportRecipientDto> => {
    const response = await apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).put(
      MONTHLY_REPORTS_RECIPIENT_PATH,
      recipient
    );
    return response.data as MonthlyReportRecipientDto;
  };

  // ---------------------------------------------------------------------------------------------
  // Advisor operations.
  //
  // These go through Core's own MonthlyReportsController (`api/MonthlyReports`) rather than the
  // YARP path above, so they are generated into `wint-api-client` and typed from the C# contract.
  // Every one of them is `[RoleAuthorize(Relations = Roles.Advisor)]` server-side — including the
  // GET — which is why `getReport` above still exists separately: the customer reading their own
  // approved report has no Advisor relation and could not call `getAdvisorReport`.
  // ---------------------------------------------------------------------------------------------

  /**
   * The company's reports, newest first. Returns the raw response rather than `.data` because
   * `TableAsync` takes the whole `ListResponse` — it reads the paging envelope, not just the rows.
   */
  const listReports = (
    baseFilter: WintUtilsWebApi.Filter
  ): Promise<AxiosResponse<WintUtilsWebApi.ListResponse<WintSuperkollAPIModel.MonthlyReportSummary>>> =>
    apiClientInstance(AuthEnums.AuthType.Company).MonthlyReportsClient.List(baseFilter);

  /**
   * Reads a report in any state, history included. This is the endpoint to poll after generating:
   * the status moves from `Generating` to `Draft`, or to `Failed` if the pass threw.
   */
  const getAdvisorReport = (id: string): Promise<WintSuperkollAPIModel.MonthlyReport> =>
    apiClientInstance(AuthEnums.AuthType.Company)
      .MonthlyReportsClient.GetById(id)
      .then((res) => res.data);

  /**
   * Returns as soon as the row exists, in `Generating` — the pass itself takes 15-90 seconds in the
   * report service's background, so the caller navigates to the report and polls rather than waits.
   * A period that already has a finished report is refused with a validation error, and a busy
   * service answers 409 with the report created and retriable shortly.
   */
  const generateReport = (
    request: WintSuperkollAPIModel.GenerateMonthlyReportRequest
  ): Promise<WintSuperkollAPIModel.MonthlyReportSummary> =>
    apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
      .MonthlyReportsClient.Generate(request)
      .then((res) => res.data);

  const regenerateReport = (
    id: string,
    request: WintSuperkollAPIModel.RegenerateMonthlyReportRequest
  ): Promise<WintSuperkollAPIModel.MonthlyReport> =>
    apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
      .MonthlyReportsClient.Regenerate(id, request)
      .then((res) => res.data);

  const editReportMarkdown = (id: string, markdown: string): Promise<WintSuperkollAPIModel.MonthlyReport> =>
    apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
      .MonthlyReportsClient.EditMarkdown(id, { markdown })
      .then((res) => res.data);

  /** Publishes a customer case and notifies the customer. Refused with 400 unless the report is a draft. */
  const approveReport = (id: string): Promise<WintSuperkollAPIModel.MonthlyReport> =>
    apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
      .MonthlyReportsClient.Approve(id)
      .then((res) => res.data);

  /** Returns an approved report to draft so the customer can no longer read it. */
  const unpublishReport = (id: string): Promise<WintSuperkollAPIModel.MonthlyReport> =>
    apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
      .MonthlyReportsClient.Unpublish(id)
      .then((res) => res.data);

  /**
   * The advisor's standing instruction for this company's reports. The report service applies it to
   * every later generation and regeneration; `text` is undefined when the company has none.
   */
  const getCompanyInstruction = (): Promise<WintSuperkollAPIModel.MonthlyReportCompanyInstruction> =>
    apiClientInstance(AuthEnums.AuthType.Company)
      .MonthlyReportsClient.GetCompanyInstruction()
      .then((res) => res.data);

  /** Empty text removes the instruction. */
  const saveCompanyInstruction = (text: string): Promise<WintSuperkollAPIModel.MonthlyReportCompanyInstruction> =>
    apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
      .MonthlyReportsClient.SaveCompanyInstruction({ text })
      .then((res) => res.data);

  return {
    getReport,
    getRecipient,
    updateRecipient,
    listReports,
    getAdvisorReport,
    generateReport,
    regenerateReport,
    editReportMarkdown,
    approveReport,
    unpublishReport,
    getCompanyInstruction,
    saveCompanyInstruction,
  };
};

export default useMonthlyReportService;
